<template>
    <div>
        <!-- checked data - no remote management -->
        <div class="row" v-if="!hasRemoteManagement && ready">
            <div class="col" v-if="pendingUpgrade">
                <div class="sau-bx bx-pad text-center">
                    <p class="sau-font sm">An upgrade request is currently pending for this service</p>
                </div>
            </div>

            <template v-else-if="!provisioned">
                <div class="row">
                    <div class="col">
                        <div class="sau-bx bx-pad">
                            <h3>Your Server is still being Provisioned</h3>
                            <p>Server Provisioning usually takes 5-45 minutes. If it has taken longer than this, our support team will be investigating the issue.</p>
                            <p>If you would like assistance from one of our support staff please click the link below.</p>
                            <sau-utility-support></sau-utility-support>
                        </div>
                    </div>
                </div>
            </template>

            <div class="col" v-else>
                <sau-apiro-response type="message">
                    <template slot="message">It looks like you don't have IPMI.</template>
                    <template slot="details">
                        <p>You'll need to upgrade to enable remote management to your server.</p>
                        <b-link
                            v-b-modal.upgrade-service
                            class="sau-link-button sm"
                        >Contact Sales Now!</b-link>
                        <sau-upgrade-modal v-on:updateFlag="updateUpgradeFlag"></sau-upgrade-modal>
                    </template>
                </sau-apiro-response>
            </div>
        </div>

        <!-- fetching data -->
        <div class="row" v-else-if="!ready">
            <div class="col">
                <sau-loading>
                    <template slot="message">Fetching Remote Access</template>
                </sau-loading>
            </div>
        </div>

        <!-- Not enabled -->
        <div class="row" v-else-if="!guestAccessEnabled">
            <div class="col text-center">
                <div class="sau-bx bx-pad">
                    <h3>Guest Access</h3>
                    <p>You can enable Guest Access to your IPMI Console to allow a trusted third-party to administer your server without sharing the main IPMI password.</p>
                    <b-link
                        href="#"
                        v-b-modal.modalGuestAccess
                        class="sau-link-button sm"
                    >Enable Guest Access</b-link>
                </div>
            </div>
        </div>

        <!-- Enabled -->
        <div class="row" v-else>
            <div class="col">
                <div class="row">
                    <div class="col">
                        <p>
                            You can enable Guest Access to your IPMI Console to allow a trusted third-party to administer your server without sharing the main IPMI password.
                            <a
                                href="#"
                            >Read more about Guest Access</a>
                        </p>
                    </div>
                </div>
                <div class="row pt-4">
                    <div class="col-sm-12 col-lg-6 drop">
                        <div class="sau-bx p-6 h-100">
                            <h5>Guest Access Username</h5>
                            <span>{{ remoteManagement.thirdpartyUser }}</span>
                            <b-link
                                href="#"
                                v-b-modal.modalGuestAccess
                                class="sau-linx green sau-font sm"
                            >Change</b-link>
                        </div>
                    </div>
                    <div class="col-sm-12 col-lg-6">
                        <div class="sau-bx p-6 h-100 ">
                            <span class="mb-12">Password</span>
                            <br />
                            <div v-show="!showGuestUserPassword">
                                <span
                                    class="sau-pwd-dot space"
                                    v-for="index in remoteManagement.thirdpartyPassword.length"
                                    :key="index"
                                ></span>
                                <a
                                    href="#"
                                    class="sau-linx green sau-font sm pl-2"
                                    @click.prevent="showGuestUserPassword = !showGuestUserPassword"
                                >Show</a> /
                                <b-link
                                    href="#"
                                    v-b-modal.modalGuestAccess
                                    class="sau-linx green sau-font sm"
                                >Change</b-link>
                            </div>
                            <div v-show="showGuestUserPassword" class="clear-password">
                                <span
                                    class="space wm-hide"
                                    v-for="(char, index) in remoteManagement.thirdpartyPassword"
                                    :key="index"
                                >{{ char }}</span>
                                <span>{{error.message}}</span>
                                <a
                                    href="#"
                                    class="sau-linx green sau-font sm pl-2"
                                    @click.prevent="showGuestUserPassword = !showGuestUserPassword"
                                >Hide</a>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="row pt-6">
                    <div class="col">
                        <a
                            href="#"
                            @click.prevent="toggleThirdPartyAccess(true)"
                            class="sau-link-button sm"
                        >Disable Guest Access</a>
                    </div>
                </div>
            </div>
        </div>

        <!--  Modal -->
        <b-modal
            id="modalGuestAccess"
            centered
            title="Guest Access"
            @shown="populateForm"
            @ok="toggleThirdPartyAccess(false)"
            :ok-variant="btnColor"
            :ok-disabled="disableOk"
            ok-title="Enable Guest Access"
            cancel-variant="link"
            ref="modalShowAccessForm"
        >
            <form>
                <div class="row">
                    <div class="col">
                        <p>It may take up to 5 minutes for the Guest Access account to be created and activated.</p>
                    </div>
                </div>
                <div class="row">
                    <div class="col-sm-12 col-lg-6 offset-lg-3">
                        <!-- Create a username -->
                        <div class="form-group">
                            <label for="guestAccessUsername">Create a username</label>
                            <input
                                type="text"
                                id="guestAccessUsername"
                                name="guestAccessUsername"
                                v-model="guestAccessUsername"
                                v-on:input="$v.guestAccessUsername.$touch"
                                class="form-control"
                                :class="{'is-invalid': $v.guestAccessUsername.$error, 'is-valid': $v.guestAccessUsername.$dirty && !$v.guestAccessUsername.$invalid}"
                            >
                            <!-- Validations -->
                            <div v-show="!$v.guestAccessUsername.$dirty">
                                <small class="text-muted">* Required</small>
                            </div>
                            <!-- default message -->
                            <div
                                v-show="!$v.guestAccessUsername.$invalid && $v.guestAccessUsername.$dirty"
                            >
                                <small>&nbsp;</small>
                            </div>
                            <!-- valid message -->
                            <div
                                v-if="!$v.guestAccessUsername.required"
                                class="invalid-feedback"
                            >* Required</div>
                            <div
                                v-else-if="!$v.guestAccessUsername.minLength || !$v.guestAccessUsername.maxLength"
                                class="invalid-feedback"
                            >Must be between 3 and 15 characters</div>
                            <div
                                v-else-if="!$v.guestAccessUsername.alphaNum && $v.guestAccessUsername.$dirty"
                                class="invalid-feedback"
                            >Username must be alphanumeric</div>
                        </div>
                        <div class="form-group">
                            <label for="guestAccessPassword">Create a password</label>
                            <input
                                type="password"
                                class="form-control"
                                id="guestAccessPassword"
                                v-model="guestAccessPassword"
                                @input="$v.guestAccessPassword.$touch"
                            >
                            <password-strength :password="guestAccessPassword"></password-strength>

                            <div v-show="!$v.guestAccessPassword.$dirty">
                                <small class="text-muted">* Required</small>
                            </div>
                            <!-- default message -->
                            <div
                                v-show="!$v.guestAccessPassword.$invalid && $v.guestAccessPassword.$dirty"
                            >
                                <small>&nbsp;</small>
                            </div>
                            <!-- valid message -->
                            <div
                                v-if="!$v.guestAccessPassword.required && $v.guestAccessPassword.$dirty"
                            >
                                <small class="text-danger">* Required</small>
                            </div>
                            <div
                                v-else-if="!$v.guestAccessPassword.alphaNum && $v.guestAccessPassword.$dirty"
                            >
                                <small class="text-danger">Password must be alphanumeric</small>
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </b-modal>
    </div>
</template>

<script>
window.generatePassword = require('password-generator');
import Vuelidate from 'vuelidate';
import UpgradeServiceModal from '@js/components/utilities/_UpgradeModal';
import {
    required,
    minLength,
    maxLength,
    alphaNum
} from 'vuelidate/lib/validators';

export default {
    components: {
        'sau-upgrade-modal': UpgradeServiceModal
    },
    data() {
        return {
            ready: false,
            pendingUpgrade: false,
            provisioned: false,
            btnColor: 'success',
            guestAccessUsername: '',
            guestAccessPassword: '',
            showGuestUserPassword: false,
            error: {
                message: ''
            }
        };
    },
    validations: {
        guestAccessUsername: {
            required,
            alphaNum,
            minLength: minLength(3),
            maxLength: maxLength(15)
        },
        guestAccessPassword: {
            required,
            alphaNum
        }
    },
    created() {
        if (Object.keys(this.remoteManagement).length == 0) {
            this.startApp();
        } else {
            this.ready = true;
        }
    },
    computed: {
        disableOk() {
            if (!this.$v.$invalid) {
                this.btnColor = 'success';
                return false;
            } else {
                this.btnColor = 'light';
                return true;
            }
        }
    },
    methods: {
        startApp() {
            let accessPromise = this.getAccessDetails()
                .then(response => {
                    if (response.data.status == 'success') {
                        this.$store.commit(
                            'setRemoteManagement',
                            response.data.device
                        );
                        this.provisioned = true;
                    } else if (
                        response.data.status == 'error' &&
                        response.data.not_provisioned
                    ) {
                        this.provisioned = false;
                    }
                })
                .catch(error => {
                    // Provide catch so the promise still resolves on error
                });

            let upgradePromise = this.checkPendingUpgrade()
                .then(response => {
                    if (response.data.status == 'success') {
                        this.pendingUpgrade = response.data.pendingUpgrade;
                    }
                })
                .catch(error => {
                    // Provide catch so the promise still resolves on error
                });

            Promise.all([accessPromise, upgradePromise]).then(
                () => (this.ready = true)
            );
        },
        updateUpgradeFlag() {
            this.pendingUpgrade = true;
        },
        populateForm() {
            // populate guestaccessusername and guestaccesspassword if remote details already set
            if (this.remoteManagement.thirdpartyUser) {
                this.guestAccessUsername = this.remoteManagement.thirdpartyUser;
            }
            if (this.remoteManagement.thirdpartyPassword) {
                this.guestAccessPassword = this.remoteManagement.thirdpartyPassword;
            }
        },
        toggleThirdPartyAccess(disable = null) {
            let swalTitle = '';
            let data = {};
            let url = '';
            if (disable) {
                url =
                    '/api/services/' +
                    this.getServiceType() +
                    '/' +
                    this.getServiceId() +
                    '/access/disable';
                swalTitle = 'Disabling';
                data = {};
            } else {
                swalTitle = 'Enabling';
                url =
                    '/api/services/' +
                    this.getServiceType() +
                    '/' +
                    this.getServiceId() +
                    '/access/enable';
                data = {
                    thirdpartyUser: this.guestAccessUsername,
                    thirdpartyPassword: this.guestAccessPassword
                };
            }
            this.$swal({
                title: swalTitle + ' Guest IPMI Access',
                text: 'Please wait...',
                imageUrl: '/img/spinner-dark-gray-80.gif',
                showConfirmButton: false,
                allowOutsideClick: false
            });
            setTimeout(() => {
                axios
                    .post(url, data)
                    .then(response => {
                        if (response.data.status == 'success') {
                            this.$swal.close();
                            if (disable) {
                                let data = {
                                    thirdpartyUser: '',
                                    thirdpartyPassword: ''
                                };
                                this.$store.commit('setThirdPartyAccess', data);
                            } else {
                                let data = {
                                    thirdpartyUser: this.guestAccessUsername,
                                    thirdpartyPassword: this.guestAccessPassword
                                };
                                this.$store.commit('setThirdPartyAccess', data);
                            }
                        } else {
                            this.$swal({
                                type: 'error',
                                title: 'There seems to be a problem',
                                html:
                                    'There has been a problem configuring access. Please try again.',
                                buttonsStyling: false,
                                confirmButtonText: 'Close',
                                confirmButtonClass: 'btn btn-danger'
                            });
                        }
                    })
                    .catch(error => {
                        this.$swal({
                            type: 'error',
                            title: 'There was an error',
                            html:
                                'There has been a problem configuring access. Please try again.',
                            buttonsStyling: false,
                            confirmButtonText: 'Close',
                            confirmButtonClass: 'btn btn-danger'
                        });
                    });
            }, 2500); //Slow it down slightly
        }
    }
};
</script>
<style>
.space {
    margin: 0px 2px;
}
.form-group .Password__strength-meter {
    margin: 10px auto;
}
</style>
